import 'animate.css';

import { Grid, Menu, MenuItem } from '@mui/material';
import { withStyles } from '@mui/styles';
import { MarkerDomain, RoutalLoader } from '@nx-smartmonkey/ui';
import { useReducer, useState } from 'react';

import { Location } from '@nx-smartmonkey/shared/domain';
import { StopFlat } from '../../../../domain/stop/StopFlat';
import { useGetMapDefaults } from '../../../../hooks/map/useGetMapDefaults';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useSetSelectedProjectStops } from '../../../../hooks/stop/useSetSelectedProjectStops';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../react-components/Text';
import { INTERCOM_EVENTS, intercom } from '../../../../services/intercom';
import { getLocationFromCoordinates } from '../../../../services/requests';
import { StopsFromScratchDialog } from '../../plan-details/dialogs/StopsFromScratchDialog';
import { PlannerStopsMapMap } from './PlannerStopsMapMap';

const styles = () => ({
  rootMenu: {
    width: `240px`,
  },
  menuItem: {
    height: `42px`,
  },
});

type UnassignedStopsContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
  coordinates: {
    lat: number;
    lng: number;
  };
};

interface PlannerStopsMapProps {
  classes: {
    rootMenu: string;
    menuItem: string;
  };
  flatStops: StopFlat[];
  geoFenceFilters?: string[];
}

export const PlannerStopsMap = withStyles(styles)(({ classes, flatStops, geoFenceFilters }: PlannerStopsMapProps) => {
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { selectedStops, setSelectedStops, selectStopFromMap } = useSetSelectedProjectStops();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { getZoom, getCoordinates } = useGetMapDefaults();
  const [contextAnchor, setContextAnchor] = useState<UnassignedStopsContextAnchor | undefined>(undefined);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);

  const [createStopModal, setCreateStopModal] = useReducer((prevCreateStopModal) => {
    if (prevCreateStopModal) {
      setSelectedLocation(undefined);
    }
    return !prevCreateStopModal;
  }, false);

  const onSelectionDrawn = (coordinates: { lat?: number; lng?: number }[]) => {
    const multipleSelection = flatStops!.filter((stop) => {
      if (stop.lat && stop.lng) {
        return MarkerDomain.isMarkerInsideCoordinates({
          markerCoords: { lat: stop.lat, lng: stop.lng },
          coordinates,
        });
      }
      return false;
    });
    setSelectedStops(multipleSelection.map((flatStop) => flatStop.id));
  };

  if (!activeProject) {
    return null;
  }

  return (
    <>
      <Grid
        container
        direction="column"
        style={{ padding: `22px`, paddingTop: 0, height: `calc(100vh - 207px)`, width: `100%` }}
        wrap="nowrap"
      >
        <div
          style={{
            position: `relative` as any,
            height: `100%`,
            width: `100%`,
          }}
        >
          <div
            style={{
              position: `absolute` as any,
              top: 0,
              left: 0,
              height: `100%`,
              width: `100%`,
            }}
          >
            <PlannerStopsMapMap
              height="100%"
              reboundsControl
              center={
                (!userCredentials?.subscription.test(`planner.geofences`) ||
                  !activeProject.geo_fences_enabled ||
                  (projectGeoFences ?? []).filter((geo) => geo.enabled).length === 0) &&
                flatStops!.length === 0
                  ? getCoordinates()
                  : undefined
              }
              zoomLevel={
                (!userCredentials?.subscription.test(`planner.geofences`) ||
                  !activeProject.geo_fences_enabled ||
                  (projectGeoFences ?? []).filter((geo) => geo.enabled).length === 0) &&
                flatStops!.length === 0
                  ? getZoom()
                  : undefined
              }
              zoomControl
              disableDoubleClickZoom
              onDrawableSelection={onSelectionDrawn}
              onContextualMenu={async (event: any) => {
                intercom.track(INTERCOM_EVENTS.plan.overview.map.contextual_clicked);
                try {
                  const result = await getLocationFromCoordinates([
                    { lat: event.latLng.lat(), lng: event.latLng.lng() },
                  ]);
                  setSelectedLocation(result[0]);
                } catch (error) {
                  setSelectedLocation(undefined);
                }
                setContextAnchor({
                  screenPosition: {
                    top: event.domEvent.y,
                    left: event.domEvent.x,
                  },
                  coordinates: {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng(),
                  },
                });
              }}
              flatStops={flatStops}
              markerOnClick={(e: any, id: string) => {
                selectStopFromMap({ stopId: id, withControlKey: e.domEvent.ctrlKey || e.domEvent.metaKey });
              }}
              markerOnDoubleClick={(e: any, id: string) => {
                setRightPanelInfo({
                  detailType: `stop`,
                  detailId: `${id}`,
                  detailTab: null,
                });
              }}
              geoFencesEnabled={
                userCredentials?.subscription.test(`planner.geofences`) && activeProject.geo_fences_enabled
              }
              geoFenceFilters={(projectGeoFences ?? []).filter(
                (geo) => geo.enabled && geoFenceFilters?.includes(geo.id)
              )}
              projectZones={(projectGeoFences ?? []).filter((geo) => geo.enabled)}
              selectedStopsIds={selectedStops}
            />

            {/* CONTEXT MENU */}
            {contextAnchor && (
              <Menu
                onClose={() => {
                  setContextAnchor(undefined);
                  setSelectedLocation(undefined);
                }}
                open={Boolean(contextAnchor)}
                anchorReference="anchorPosition"
                anchorPosition={contextAnchor && contextAnchor.screenPosition}
                classes={{ list: classes.rootMenu }}
              >
                {selectedLocation === undefined && <RoutalLoader />}
                {selectedLocation === null && (
                  <Text variant="body" tagName="p" style={{ padding: `16px` }} intlId="location_nothing_found" />
                )}
                {selectedLocation && (
                  <Grid container direction="column">
                    <p style={{ padding: `16px`, fontSize: `11px` }}>{selectedLocation.label}</p>
                    <MenuItem
                      className={classes.menuItem}
                      // TODO: CONTROL IF LIMIT HAS BEEN REACHED
                      // disabled={stops.length >= (projectMaxServices ?? 0)}
                      onClick={() => {
                        setContextAnchor(undefined);
                        setCreateStopModal();
                      }}
                    >
                      <Text style={{ cursor: `pointer` }} intlId="properties.service.map.create_new" />
                    </MenuItem>
                  </Grid>
                )}
              </Menu>
            )}
          </div>
        </div>
      </Grid>

      <StopsFromScratchDialog
        open={createStopModal}
        initialLocation={selectedLocation}
        handleCloseDialog={setCreateStopModal}
      />
    </>
  );
});
